import React from 'react';
import { ChevronDown, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { theme } from '../../theme';

const DepartmentCard = ({ 
  dept, 
  deptProgress, 
  isExpanded, 
  toggleDepartment, 
  formatDate, 
  updateTaskStatus, 
  viewMode 
}) => {
  // Status badge for tasks
  const StatusBadge = ({ status }) => {
    switch(status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" /> Complete
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock size={12} className="mr-1" /> In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle size={12} className="mr-1" /> Not Started
          </span>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* Department header */}
      <div 
        className="p-4 flex justify-between items-center cursor-pointer transition-colors duration-200 hover:bg-gray-50"
        onClick={() => toggleDepartment(dept.name)}
      >
        <div className="flex items-center">
          <div className="mr-3 bg-green-50 p-2 rounded-lg text-green-600 transition-all duration-300">
            {dept.icon}
          </div>
          <div>
            <h3 className="font-medium text-gray-800">{dept.name}</h3>
            <div className="text-sm text-gray-500">{dept.tasks.length} tasks</div>
          </div>
        </div>
        <div className="flex items-center">
          <span className="mr-2 font-medium text-gray-800">{deptProgress}%</span>
          <div className="transition-transform duration-300 bg-gray-50 w-8 h-8 rounded-full flex items-center justify-center" 
               style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <ChevronDown size={16} className="text-gray-500" />
          </div>
        </div>
      </div>
      
      {/* Progress bar with animation */}
      <div className="h-1 bg-gray-100">
        <div 
          className="h-1 transition-all duration-1000 ease-out"
          style={{ 
            width: `${deptProgress}%`,
            backgroundColor: deptProgress === 100 ? theme.colors.status.success : 
                            deptProgress > 50 ? theme.colors.secondary.accent : 
                            theme.colors.primary.main
          }}
        ></div>
      </div>
      
      {/* Expanded task list with animation */}
      <div 
        className="overflow-hidden transition-all duration-500 ease-in-out bg-gray-50"
        style={{ 
          maxHeight: isExpanded ? (viewMode === 'mobile' ? '300px' : '500px') : '0px',
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div className="p-3">
          {dept.tasks.length > 0 ? (
            <div className="space-y-2">
              {dept.tasks.map((task, index) => (
                <div 
                  key={task.id || task.name} 
                  className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm transition-all hover:shadow-md"
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: 'both'
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                    <div className="font-medium text-gray-800 mb-2 sm:mb-0">{task.name}</div>
                    <div 
                      className="cursor-pointer" 
                      onClick={() => {
                        if (task.id) {
                          const nextStatus = task.status === 'not-started' ? 'in-progress' : 
                                          task.status === 'in-progress' ? 'completed' : 'not-started';
                          updateTaskStatus(task.id, nextStatus);
                        }
                      }}
                    >
                      <StatusBadge status={task.status} />
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <div>Assigned to: <span className="text-gray-800">{task.assignee}</span></div>
                    <div>Due: <span className="text-gray-800">{formatDate(task.due)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              No tasks in this department
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepartmentCard;
